import { format, parseISO, subDays } from "date-fns";
import { useMemo } from "react";

import { useGetTimeAttendanceSummaryQuery } from "@/services/(my-space)/api.time-attendance";
import { useMsGetAllLeaveRequestsQuery } from "@/services/(my-space)/api.leaves";
import { DAY_TYPE_ENUM, LeaveRequestStatus } from "@/utils/enums";
import { useServerToday } from "@/hooks/useServerToday";

const ACTIVE_LEAVE_STATUSES: string[] = [LeaveRequestStatus.PENDING, LeaveRequestStatus.APPROVED];

/** True once we know yesterday was a working day with no clock-in and no pending/approved leave covering it. */
export const useYesterdayAttendanceGap = () => {
  const today = useServerToday();
  const yesterday = useMemo(() => subDays(parseISO(today), 1), [today]);
  const yesterdayYMD = format(yesterday, "yyyy-MM-dd");

  const { data: summaryData, isLoading: isLoadingSummary } = useGetTimeAttendanceSummaryQuery({
    start: yesterdayYMD,
    end: yesterdayYMD,
  });
  const { data: leaveData, isLoading: isLoadingLeaves } = useMsGetAllLeaveRequestsQuery({
    startDate: yesterdayYMD,
    endDate: yesterdayYMD,
    limit: 5,
  });

  const isLoading = isLoadingSummary || isLoadingLeaves;

  const yesterdaySummary = summaryData?.data?.days?.find((day) => day.date === yesterdayYMD);
  const wasAbsent =
    yesterdaySummary?.status === "absent" &&
    yesterdaySummary?.dayType !== DAY_TYPE_ENUM.WEEKEND &&
    !yesterdaySummary?.isHoliday;

  const hasActiveLeaveRequest = (leaveData?.data ?? []).some((leave) =>
    ACTIVE_LEAVE_STATUSES.includes(leave.status),
  );

  return {
    isLoading,
    yesterday,
    hasGap: !isLoading && Boolean(wasAbsent) && !hasActiveLeaveRequest,
  };
};
